import { useQuery } from '@tanstack/react-query'
import { dataAPI } from '@/core/api'
import {
  availableUnitsOf,
  baseUnitOf,
  convertSync,
  extractConversions,
  normalizeConversionsResponse,
  type ConversionsResponse,
} from '@/features/summary-statistics/unitConversions'

/** The same key the Vue composable uses: one cache entry, whoever fetched it. */
export const unitConversionsQueryKey = (workspaceId: number, scenarioId: number | null) =>
  ['unit-conversions', workspaceId, scenarioId] as const

const EMPTY: ConversionsResponse = { conversions: [] }

// client.js is plain JavaScript and its `scenarioId = null` default makes TS
// infer the parameter as `null`. One cast at the boundary until the client is
// ported (spec §6.2: "ports with an extension rename").
const getUnitConversions = dataAPI.getUnitConversions as (scenarioId: number | null) => Promise<ConversionsResponse>

/** The units this workspace and scenario can display, base unit first. */
export function useUnitConversions(scenarioId: number | null, workspaceId: number) {
  const { data } = useQuery({
    queryKey: unitConversionsQueryKey(workspaceId, scenarioId),
    queryFn: async () =>
      normalizeConversionsResponse(await getUnitConversions(scenarioId)),
    staleTime: 5 * 60 * 1000,
    enabled: !!workspaceId,
    placeholderData: EMPTY,
  })
  const conversions = extractConversions(data)
  const baseUnit = baseUnitOf(data, conversions)
  return {
    baseUnit,
    availableUnits: availableUnitsOf(baseUnit, conversions),
    conversions,
    /**
     * null when there is no path between the units — never the unconverted
     * value. The stat cards rely on that to keep the base unit and its label
     * rather than mislabelling a number they did not convert.
     */
    convert: (value: number, from: string, to: string) => convertSync(conversions, value, from, to),
  }
}
